<template>
  <div id="order-label-print">
    <div v-for="label in labels" :key="label.key" class="barcode-label">
      <svg v-if="label.encodable" class="barcode" preserveAspectRatio="none" jsbarcode-format="code128" :jsbarcode-value="label.barcodeValue"
        jsbarcode-width="2" jsbarcode-height="60" jsbarcode-margin="0" jsbarcode-marginleft="20" jsbarcode-marginright="20" jsbarcode-displayvalue="false"></svg>
      <div v-if="label.sku" class="label-sku">{{ label.sku }}</div>
      <div v-if="label.name" class="label-name">{{ label.name }}</div>
      <!-- Captions stay in English in every UI language, so a label reads the same for everyone handling the product -->
      <div v-if="label.color || label.size" class="label-attributes">
        <div v-if="label.color">Color: <b>{{ label.color }}</b></div>
        <div v-if="label.size">Size: <b>{{ label.size }}</b></div>
      </div>
      <div v-if="label.batch" class="label-batch">Batch: <b>{{ label.batch }}</b></div>
    </div>
  </div>
</template>

<script>
import i18nData from '../../../i18n/i18nData'

// Missing, null, empty or non-text values print nothing, and their line is left out
const text = (value) => {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
}

export default {
  props: ['selected'],
  computed: {
    labels () {
      return this.selected.map((product, index) => {
        const sku = text(product.sku)
        const color = text(product.color)
        return {
          key: product.id || sku || index,
          sku,
          // Encoded exactly as stored so a scan matches the product's SKU
          barcodeValue: String(product.sku),
          // JsBarcode throws on values Code128 can't encode, which would stop every label from printing
          encodable: sku !== '' && /^[ -~]+$/.test(String(product.sku)),
          name: text(product.name),
          // Some products store their color as a hex code, which means nothing on a printed label
          color: color.includes('#') ? '' : color,
          size: text(product.size),
          batch: text(product.batch_id)
        }
      })
    }
  },
  methods: {
    warnMissingBarcodes () {
      const missing = this.labels.filter(label => !label.encodable)
      if (missing.length === 0) return
      this.$vs.notify({
        color: 'warning',
        title: i18nData[this.$i18n.locale]['Warning'],
        text: `${i18nData[this.$i18n.locale]['These labels have no barcode because the SKU is missing or has characters a barcode cannot hold:']} ${missing.map(label => label.sku || label.name).join(', ')}`,
        position: 'top-center',
        time: 10000
      })
    }
  },
  mounted () {
    // @page can't be limited with a selector, so the label page size only exists while this component does
    this.pageStyle = document.createElement('style')
    this.pageStyle.textContent = '@media print { @page { size: 1.5in 1in; margin: 0; } }'
    document.head.appendChild(this.pageStyle)
    window.addEventListener('afterprint', this.warnMissingBarcodes)
  },
  beforeDestroy () {
    this.pageStyle.remove()
    window.removeEventListener('afterprint', this.warnMissingBarcodes)
  }
}
</script>

<style lang="scss">
@media print{
  html:has(.barcode-label),
  body:has(.barcode-label) {
    width: 1.5in;
    margin: 0;
  }
  // Take everything except the labels out of the layout, and flatten the
  // wrappers around them. Content that is only visibility: hidden still takes
  // up space, so Chrome shrinks the labels to fit the page width and prints
  // blank pages after them.
  body:has(.barcode-label) {
    &,
    :has(.barcode-label) {
      > :not(:has(.barcode-label)):not(.barcode-label) {
        display: none !important;
      }
    }
    :has(.barcode-label) {
      display: block !important;
      position: static !important;
      width: auto !important;
      min-width: 0 !important;
      height: auto !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }
  }
  #shipblu-chat-launcher,
  #shipblu-chat-frame,
  #fc_frame {
    display: none;
  }
  .shipment-page,
  .dropdown-menu{
    *{
      visibility: hidden;
      color: black;
    }
    #order-label-print,
    #order-label-print *{
      visibility: visible;
    }
    #order-label-print{
      display: block;
    }
    #content-area {
      margin: 0 !important;
    }
    #order-label-print {
      position: absolute;
      left: 0;
      top: 0;
      box-shadow: none;
    }
    .barcode-label {
      width: 1.5in;
      height: 1in;
      margin: 0 !important;
      padding: 0.05in 0.06in 0.04in;
      display: flex;
      flex-direction: column;
      justify-content: center;
      row-gap: 0.012in;
      overflow: hidden;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 6pt;
      line-height: 1.12;
      letter-spacing: normal;
      page-break-before: auto;
      page-break-after: always;
      page-break-inside: avoid;
      break-before: auto;
      break-after: page;
      break-inside: avoid;
      box-sizing: border-box;
      > * {
        flex: none;
        margin: 0;
      }
      b {
        font-weight: bold;
      }
    }
    .barcode-label:last-child {
      page-break-after: auto;
      break-after: auto;
    }
    // The barcode takes whatever height the text lines leave free
    .barcode-label .barcode {
      display: block;
      width: 100%;
      flex: 1 1 0.28in;
      min-height: 0.28in;
      max-height: 0.5in;
    }
    .label-sku {
      font-size: 5.5pt;
      line-height: 1;
      text-align: center;
      letter-spacing: 0.03em;
    }
    .label-name {
      font-weight: bold;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      overflow-wrap: anywhere;
    }
    .label-attributes {
      display: flex;
      flex-wrap: wrap;
      column-gap: 0.08in;
    }
    .label-sku,
    .label-attributes > div,
    .label-batch {
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
